/**
 * Theme Selector Component
 * 
 * Dropdown component for selecting color themes.
 * Shows system themes and user's custom themes grouped by type.
 */

import React, { useEffect, useState } from 'react';
import { Select, SelectProps } from '@cloudscape-design/components';
import { ColorTheme } from '../../types/themes';
import { listUserThemes } from '../../services/themeApi';

interface ThemeSelectorProps {
  dataProduct: string;
  attribute: string;
  selectedThemeId?: string;
  onThemeChange: (themeId: string, theme: ColorTheme) => void;
  disabled?: boolean;
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({
  dataProduct,
  attribute,
  selectedThemeId,
  onThemeChange,
  disabled = false,
}) => {
  const [themes, setThemes] = useState<ColorTheme[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<SelectProps.Option | null>(null);

  useEffect(() => {
    // Reset selection when product/attribute changes
    setSelectedOption(null);
    setThemes([]);
    loadThemes();
  }, [dataProduct, attribute]);

  useEffect(() => {
    // Update selected option when selectedThemeId changes or themes load
    if (themes.length === 0) return;

    if (selectedThemeId) {
      const theme = themes.find(t => t.theme_id === selectedThemeId);
      if (theme) {
        setSelectedOption({
          label: formatThemeLabel(theme),
          value: theme.theme_id,
        });
      }
    } else {
      // Auto-select default theme when no theme is selected
      const defaultTheme = themes.find(t => t.is_default);
      if (defaultTheme) {
        setSelectedOption({
          label: formatThemeLabel(defaultTheme),
          value: defaultTheme.theme_id,
        });
        onThemeChange(defaultTheme.theme_id, defaultTheme);
      }
    }
  }, [selectedThemeId, themes]);

  const loadThemes = async () => {
    if (!dataProduct || !attribute) return;

    setLoading(true);
    try {
      const fetchedThemes = await listUserThemes(dataProduct, attribute);
      setThemes(fetchedThemes);
      // Theme selection is handled by the useEffect that watches themes array
    } catch (error) {
      console.error('Error loading themes:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatThemeLabel = (theme: ColorTheme): string => {
    const typeLabel = theme.theme_type === 'system' ? '[System]' : '[Custom]';
    const defaultLabel = theme.is_default ? ' (Default)' : '';
    return `${theme.theme_name} ${typeLabel}${defaultLabel}`;
  };

  const handleChange = (detail: SelectProps.ChangeDetail) => {
    const option = detail.selectedOption;
    setSelectedOption(option);

    if (option && option.value) {
      const theme = themes.find(t => t.theme_id === option.value);
      if (theme) {
        onThemeChange(theme.theme_id, theme);
      }
    }
  };

  // Group themes by type
  const systemThemes = themes.filter(t => t.theme_type === 'system');
  const userThemes = themes.filter(t => t.theme_type === 'user');

  const options: SelectProps.Option[] = [];

  if (systemThemes.length > 0) {
    // Add system themes as individual options
    systemThemes.forEach(theme => {
      options.push({
        label: `${theme.theme_name}${theme.is_default ? ' (Default)' : ''}`,
        value: theme.theme_id,
        tags: ['System'],
      });
    });
  }

  if (userThemes.length > 0) {
    // Add user themes as individual options
    userThemes.forEach(theme => {
      options.push({
        label: theme.theme_name,
        value: theme.theme_id,
        tags: ['Custom'],
      });
    });
  }

  return (
    <Select
      selectedOption={selectedOption}
      onChange={({ detail }) => handleChange(detail)}
      options={options}
      placeholder="Select a theme"
      disabled={disabled || !dataProduct || !attribute}
      loadingText="Loading themes..."
      statusType={loading ? 'loading' : 'finished'}
      data-testid="theme-selector"
    />
  );
};

export default ThemeSelector;
